// Times as the client reads and shows them. parseTime/compareTimes are the
// engine's time order (crates/engine/src/attention.rs parse_time).

/** Milliseconds since the epoch of an ISO-8601 time, or null (the engine's
 *  parse_time: `YYYY-MM-DDTHH:MM:SS[.fff…](Z|±HH:MM)`, a space may replace T). */
export function parseTime(at: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2}):(\d{2})(?:\.(\d*))?(Z|z|[+-]\d{2}:?\d{2})?$/.exec(at.trim());
  if (!m) return null;
  const [year, month, day, hour, minute, second] = m.slice(1, 7).map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 60) return null;
  const ms = Number(((m[7] ?? '') + '000').slice(0, 3));
  let offset = 0;
  const zone = m[8];
  if (zone && zone !== 'Z' && zone !== 'z') {
    const body = zone.slice(1).replace(':', '');
    offset = (zone[0] === '-' ? -1 : 1) * (Number(body.slice(0, 2)) * 60 + Number(body.slice(2)));
  }
  const first = new Date(0);
  first.setUTCFullYear(year, month - 1, 1); // years below 100 as written
  const days = first.getTime() / 86_400_000 + day - 1;
  return ((days * 86_400 + hour * 3600 + minute * 60 + second - offset * 60) * 1000) + ms;
}

/**
 * Orders times by instant (the engine's order); unparseable ones sort
 * first, as strings among themselves. Compare times with this, never as
 * strings: `…:00Z` sorts after `…:00.001Z` as text.
 */
export function compareTimes(a: string, b: string): number {
  const x = parseTime(a);
  const y = parseTime(b);
  if (x !== null && y !== null) return x - y;
  if (x === null && y !== null) return -1;
  if (x !== null && y === null) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function relTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.round((now - t) / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 45) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
